import React, { useState, useEffect, useMemo, useRef } from "react";
import LoadingOverlay from "./LoadingOverlay";
import "./summarypage.css";
import { FaDownload } from "react-icons/fa";
import { faCircleExclamation, faSliders, faLocationDot, faBullseye } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useLocation, useNavigate } from "react-router-dom";
import { paginateCanvasToPdf } from "./pdfBreakHelper";
import { buildDomainTagsFromDataMap } from "./goalDomainTags";
import { API_BASE, assetUrl } from "../apiConfig";

const isTokenError = (json) => {
  if (!json || json.status !== false || !json.message) return false;
  const msg = json.message.toLowerCase();
  return (msg.includes("invalid") && msg.includes("token"))
    || (msg.includes("expired") && msg.includes("token"))
    || msg.includes("token already logged out")
    || msg.includes("token missing");
};

const dispatchSessionExpired = () => {
  window.dispatchEvent(new CustomEvent("session-expired"));
};

// ---------------------------------------------------------------------------
// Intervention description rendering
//
// intervention_description is authored in a rich text editor, so an entry can
// carry formatting markup: <b>/<strong>, <i>/<em>, <u>, <a href>, <br>, lists,
// <p> ... The "Download Summary" button snapshots the LIVE DOM with
// html2canvas, so any markup that is printed as plain text on screen is also
// plain text in the PDF. Rendering the markup (allow-listed, so nothing
// executable comes through) in both the on-screen and the PDF branch is what
// makes bold / italic / underline / links / line breaks survive the download.
// ---------------------------------------------------------------------------

// Tags kept when sanitizing. Anything else is unwrapped (its text survives,
// the tag does not), so unknown/legacy markup can never break the layout.
const ALLOWED_TAGS = new Set([
  "a", "b", "blockquote", "br", "code", "del", "div", "em", "h1", "h2", "h3",
  "h4", "h5", "h6", "hr", "i", "ins", "li", "mark", "ol", "p", "pre", "s",
  "small", "span", "strike", "strong", "sub", "sup", "u", "ul",
]);

// Inline style properties kept, so pasted content that expresses bold/italic
// through a style attribute (instead of <b>/<i>) still renders.
const ALLOWED_STYLE_PROPS = new Set([
  "background-color", "color", "font-family", "font-size", "font-style",
  "font-weight", "list-style-type", "margin-left", "padding-left", "text-align",
  "text-decoration", "text-decoration-line", "vertical-align",
]);

// Cheap test for "this entry is markup, not prose".
const FORMATTING_MARKUP_RE =
  /<\s*\/?\s*(a|b|strong|i|em|u|s|strike|del|ins|p|div|span|br|ul|ol|li|h[1-6]|blockquote|small|sup|sub|mark|code|pre|hr)\b[^>]*>/i;

const URL_IN_TEXT_RE = /((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const readHtmlAttr = (attrs, name) => {
  const match = String(attrs || "").match(
    new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i")
  );
  if (!match) return "";
  return match[2] ?? match[3] ?? match[4] ?? "";
};

// Only http(s)/mailto/in-page targets survive; "javascript:" and friends are
// dropped rather than escaped.
const sanitizeHref = (href) => {
  const value = String(href || "")
    .trim()
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "");
  if (!value) return "";
  if (/^(https?:\/\/|mailto:|\/|#)/i.test(value)) return value;
  if (/^www\./i.test(value)) return `https://${value}`;
  return "";
};

const sanitizeInlineStyle = (styleAttr) => {
  const kept = [];
  String(styleAttr || "")
    .split(";")
    .forEach((decl) => {
      const sep = decl.indexOf(":");
      if (sep === -1) return;
      const prop = decl.slice(0, sep).trim().toLowerCase();
      const value = decl.slice(sep + 1).trim();
      if (!prop || !value) return;
      if (!ALLOWED_STYLE_PROPS.has(prop)) return;
      if (/url\s*\(|expression\s*\(|javascript:/i.test(value)) return;
      kept.push(`${prop}: ${value}`);
    });
  return kept.join("; ");
};

const sanitizeRichHtml = (html) =>
  String(html)
    // Drop executable / layout-breaking elements including their content.
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta|noscript|form)\b[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi,
      ""
    )
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(
      /<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)((?:"[^"]*"|'[^']*'|[^"'>])*?)\/?\s*>/g,
      (match, closing, tag, attrs) => {
        const name = tag.toLowerCase();
        if (!ALLOWED_TAGS.has(name)) return "";
        if (name === "br") return "<br />";
        if (closing) return `</${name}>`;
        if (name === "a") {
          const href = sanitizeHref(readHtmlAttr(attrs, "href"));
          return href
            ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer" class="interv-desc-link">`
            : "<a>";
        }
        const style = sanitizeInlineStyle(readHtmlAttr(attrs, "style"));
        return style ? `<${name} style="${escapeHtml(style)}">` : `<${name}>`;
      }
    );

// "https://x.com/y." -> { shown: "https://x.com/y", href: "https://x.com/y" }
// so a sentence-ending full stop is not swallowed into the link target.
const splitTrailingPunctuation = (url) => {
  const trailing = (String(url).match(/[.,;:!?)]+$/) || [""])[0];
  const shown = trailing ? String(url).slice(0, url.length - trailing.length) : String(url);
  return { shown, trailing, href: /^www\./i.test(shown) ? `https://${shown}` : shown };
};

const anchorProps = `target="_blank" rel="noopener noreferrer" class="interv-desc-link"`;

// Plain prose: keep the author's line breaks and turn any bare URL into a
// link (the backend appends citation links straight into the description).
const renderPlainTextWithLinks = (text) => {
  const nodes = [];
  let lastIndex = 0;
  text.replace(URL_IN_TEXT_RE, (match, _group, offset) => {
    nodes.push(text.slice(lastIndex, offset));
    const { shown, trailing, href } = splitTrailingPunctuation(match);
    if (shown) {
      nodes.push(
        <a key={`link-${offset}`} className="interv-desc-link" href={href} target="_blank" rel="noopener noreferrer">
          {shown}
        </a>
      );
    }
    if (trailing) nodes.push(trailing);
    lastIndex = offset + match.length;
    return match;
  });
  nodes.push(text.slice(lastIndex));
  return nodes;
};

// Markup can still carry a bare citation URL as text (the backend appends it
// to the description). Walk the text between tags so already-linked
// <a>...</a> blocks are left untouched.
const linkifyBareUrlsInHtml = (html) =>
  html.replace(/(<a\b[^>]*>[\s\S]*?<\/a>)|([^<]+)/gi, (match, anchor, text) => {
    if (anchor || !text) return match;
    return text.replace(URL_IN_TEXT_RE, (url) => {
      const { shown, trailing, href } = splitTrailingPunctuation(url);
      if (!shown) return url;
      return `<a href="${escapeHtml(href)}" ${anchorProps}>${escapeHtml(shown)}</a>${trailing}`;
    });
  });

// Renders one intervention_description entry (or the whole string).
const renderDescriptionText = (desc, index, style, className) => {
  const text = desc == null ? "" : String(desc).trim();
  if (!text) return null;

  // A <div> (never a <p>) so block level markup coming from the editor
  // (<p>, <ul>, <div>) does not get auto-closed by the HTML parser.
  if (FORMATTING_MARKUP_RE.test(text)) {
    return (
      <div
        key={index}
        className={`interv-desc-rich ${className || ""}`.trim()}
        style={style}
        dangerouslySetInnerHTML={{ __html: linkifyBareUrlsInHtml(sanitizeRichHtml(text)) }}
      />
    );
  }

  return (
    <div
      key={index}
      className={className}
      style={{ whiteSpace: "pre-line", ...(style || {}) }}
    >
      {renderPlainTextWithLinks(text)}
    </div>
  );
};

export default function Interventionspage() {
  const navigate = useNavigate();
  const [interventionsData, setInterventionsData] = useState(null);
  const [activeGoalTab, setActiveGoalTab] = useState(null);
  const [accordionOpen, setAccordionOpen] = useState({});
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const sessionId = urlParams.get("sessionId");

  const isFromReview = urlParams.get("fromReview") === "1";
  // Review Smart Goal pathway: the session that actually holds the goal being
  // revised. The Interventions page is opened against the review session, but
  // "Revise Selected Intervention" has to write to the goal session so the
  // existing goal is updated rather than a second goal being created.
  //
  // The URL param is the fast path but NOT the only way onto this page. A Review
  // Smart Goal session opened without `smartGoal=1` - a bookmark, a refresh, or a
  // link that dropped the param - came back with isSmartGoal false, so the button
  // rendered "Set Goal Procedure" and offered to CREATE a second goal against a
  // session whose whole purpose is revising the first one.
  //
  // get_test_intervention.php already reports the session's appointment_type
  // (lower-cased, `session.appointment_type`), so fall back to that: the session
  // is the authority on which pathway it is, and a URL param cannot contradict
  // it. OR semantics, so an explicit ?smartGoal=1 still wins for a session whose
  // appointment_type has not loaded yet.
  const smartGoalFromUrl = urlParams.get("smartGoal") === "1";
  const smartGoalFromSession =
    String(interventionsData?.session?.appointment_type || "")
      .trim()
      .toLowerCase() === "review smart goal";
  const isSmartGoal = smartGoalFromUrl || smartGoalFromSession;
  const goalSessionId = urlParams.get("goalSessionId") || urlParams.get("goalSession");
  const reviseSessionId = isSmartGoal && goalSessionId ? goalSessionId : sessionId;

  console.log(sessionId);
  const [session, setSession] = useState("");
  const [showShareModal, setShowShareModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [activeSheet, setActiveSheet] = useState(null);
  const [activeIntTitle, setActiveIntTitle] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const summaryRef = useRef(null);
  const scrollContentRef = useRef(null);
  const observerRef = useRef(null);
  const token = localStorage.getItem("token");

  // -- 1. Session ID Initialization --------------------------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlSessionId = urlParams.get("sessionId");

    if (urlSessionId) {
      setCurrentSessionId(urlSessionId);
      return;
    }

    const savedState = localStorage.getItem("assessmentState");
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        setSession(parsed.session || "");
        if (parsed.currentSessionId) {
          setCurrentSessionId(parsed.currentSessionId);
        }
      } catch (e) {
        console.error("Error parsing saved state", e);
      }
    }
  }, [location.search]);

  // -- 2. Fetch Data ------------------------------------------------------------
  const fetchInterventions = async () => {
    if (!currentSessionId) return;

    setInterventionsData(null);
    setActiveGoalTab(null);
    setAccordionOpen({});
    setActiveSheet(null);
    setActiveIntTitle(null);

    try {
      const response = await fetch(
        `${API_BASE}/therapist_api/get_test_intervention.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            session_id: parseInt(currentSessionId),
            // Review Smart Goal: the catalogue keys inherit the domains/skills
            // from the session that owns the goal being reviewed, so tell the
            // backend which one that is.
            ...(isSmartGoal && goalSessionId ? { goal_session_id: parseInt(goalSessionId) } : {}),
          }),
        }
      );
      

      const json = await response.json();

      if (json.status && json.data) {
        setInterventionsData(json);

        const goalTabs = Object.keys(json.data || {}).map((key) => {
          const items = json.data[key] || [];
          const firstItem = items[0] || {};
          const goalDomain = firstItem.goal_domain || "";
          const goalSkill = firstItem.goal_skill || "";
          const tabKey = goalSkill && goalSkill !== "null" ? goalSkill : goalDomain;
          const displayLabel = goalSkill && goalSkill !== "null"
            ? `${goalDomain} - ${goalSkill}`
            : goalDomain;

          return {
            key,
            tabKey,
            displayLabel,
          };
        });

        const uniqueGoalTabs = goalTabs.filter(
          (tab, index, self) =>
            index === self.findIndex((t) => t.tabKey === tab.tabKey)
        );

        if (uniqueGoalTabs.length > 0) {
          const firstTab = uniqueGoalTabs[0];
          setActiveGoalTab(firstTab.tabKey);
          const firstTabInterventions = json.data[firstTab.key] || [];
          const defaultOpen = {};
          firstTabInterventions.slice(0, 2).forEach((item, idx) => {
            const id = item.id || idx;
            defaultOpen[`int-${id}`] = true;
          });
          setAccordionOpen(defaultOpen);
        }

      } else {
        if (isTokenError(json)) { dispatchSessionExpired(); return; }
      }
    } catch (error) {
      console.error("Error fetching interventions:", error.message);
    }
  };

  // Deliberately keyed on the session only: re-running this on every render
  // would refetch (and reset the accordion state) on each keystroke elsewhere.
  useEffect(() => {
    if (currentSessionId) {
      fetchInterventions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSessionId]);

  // Set default active sheet & first intervention title when activeGoalTab changes
  useEffect(() => {
    if (!interventionsData?.data || !activeGoalTab) return;
    const items = Object.values(interventionsData.data).flat().filter(i => {
      const tabKey = i.goal_skill && i.goal_skill !== "null" ? i.goal_skill : i.goal_domain;
      return tabKey === activeGoalTab;
    });
    const grouped = {};
    items.forEach(i => {
      const s = i.excel_sheet || "General";
      if (!grouped[s]) grouped[s] = [];
      grouped[s].push(i);
    });
    const sheets = Object.keys(grouped);
    if (sheets.length > 0) {
      setActiveSheet(sheets[0]);
      const first = grouped[sheets[0]][0];
      if (first) setActiveIntTitle(first.intervention_title || null);
    }
    // interventionsData is intentionally omitted: the tab change is the only
    // trigger, and re-running on a data refetch would stomp a manual sheet pick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGoalTab]);

  // -- Active tab's valid interventions (for scrollspy + render) -------------
  const activeTabData = useMemo(() => {
    if (!interventionsData?.data || !activeGoalTab) return { selectedTab: null, validSelected: [], grouped: {}, sheetNames: [] };

    const { data: interventionsMap } = interventionsData;
    const allTabs = Object.keys(interventionsMap || {}).map((key) => {
      const items = interventionsMap[key] || [];
      const firstItem = items[0] || {};
      const goalDomain = firstItem.goal_domain || "";
      const goalSkill = firstItem.goal_skill || "";
      const tabKey = goalSkill && goalSkill !== "null" ? goalSkill : goalDomain;
      return { key, tabKey };
    });
    const uniqueAllTabs = allTabs.filter((t, i, s) => i === s.findIndex(x => x.tabKey === t.tabKey));
    const selectedTab = uniqueAllTabs.find((t) => t.tabKey === activeGoalTab);
    const selectedInterventions = selectedTab ? interventionsMap[selectedTab.key] || [] : [];

    const hasDesc = (item) =>
      item.intervention_title &&
      item.intervention_description !== null &&
      item.intervention_description !== undefined &&
      item.intervention_description !== "" &&
      !(Array.isArray(item.intervention_description) && item.intervention_description.length === 0);

    const valid = selectedInterventions.filter(hasDesc);
    const grouped = {};
    valid.forEach((item) => {
      const sheet = item.excel_sheet || "General";
      if (!grouped[sheet]) grouped[sheet] = [];
      grouped[sheet].push(item);
    });

    return { selectedTab, validSelected: valid, grouped, sheetNames: Object.keys(grouped) };
  }, [interventionsData, activeGoalTab]);

  // -- Scrollspy: highlight sidebar items as user scrolls the right panel --
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    if (isGeneratingPdf || activeTabData.validSelected.length === 0) return;

    const container = scrollContentRef.current;
    if (!container) return;

    const visible = new Map();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visible.set(entry.target.id, entry.target.getBoundingClientRect().top);
          } else {
            visible.delete(entry.target.id);
          }
        });
        if (visible.size > 0) {
          let bestId = null;
          let bestTop = Infinity;
          visible.forEach((top, id) => {
            if (top < bestTop) { bestTop = top; bestId = id; }
          });
          if (bestId) {
            const numId = bestId.replace("intv-", "");
            const matchItem = activeTabData.validSelected.find(v => String(v.id) === String(numId));
            if (matchItem) {
              setActiveIntTitle(matchItem.intervention_title);
              setActiveSheet(matchItem.excel_sheet || "General");
            }
          }
        }
      },
      { root: container, rootMargin: "-60px 0px -50% 0px", threshold: 0 }
    );

    activeTabData.validSelected.forEach((item) => {
      const el = document.getElementById(`intv-${item.id}`);
      if (el) observerRef.current.observe(el);
    });

    return () => { if (observerRef.current) observerRef.current.disconnect(); };
  }, [activeTabData, isGeneratingPdf]);

  // -- 3. Helper Components & Functions -------------------------------------------

  // -- 4. PDF Generation Logic ---------------------------------------------------

  // Helper to dynamically load scripts
  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve(); // Already loaded
        return;
      }
      const script = document.createElement("script");
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  // Extract client details for filename from session info
  const client = interventionsData?.session?.client_id || "client";
  const ageRange = interventionsData?.session?.age_group || "-";
  const gender = interventionsData?.session?.gender || "-";

  // Function to expand all accordions temporarily for PDF capture
  // (kept mainly so accordionOpen state is sane once isGeneratingPdf resets;
  // the actual "everything open" behavior during capture comes from the
  // isGeneratingPdf flag inside AccordionItem above.)
  const openAllAccordions = () => {
    const previousState = { ...accordionOpen };
    const newState = {};

    if (interventionsData && interventionsData.data) {
      Object.values(interventionsData.data).forEach(interventionList => {
        interventionList.forEach(item => {
          if (item.id) newState[`int-${item.id}`] = true;
        });
      });
    }

    setAccordionOpen(newState);
    return previousState;
  };

  const captureSummaryAsPdfBlob = async () => {
    if (!summaryRef.current) throw new Error("Summary element not found");

    // Dynamically load libraries if not present
    try {
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js");
      await loadScript("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js");
    } catch (err) {
      throw new Error("Failed to load PDF libraries.");
    }

    // Double-check global objects
    if (!window.html2canvas || !window.jspdf) {
      throw new Error("PDF libraries are still not available on window object.");
    }

    // Wait for React to re-render the component with images hidden,
    // ALL goal-domain sections rendered, and all accordions open.
    await new Promise(r => setTimeout(r, 800));

    const element = summaryRef.current;

    // Capture the element as an image. Note: images are hidden during PDF
    // capture (!isGeneratingPdf && image), so the whole captured surface is
    // just text on flat/colored backgrounds - meaning there is always a
    // blank (uniform-color) gap between lines we can safely cut on.
    const canvas = await window.html2canvas(element, {
      scale: 1.5, // Higher scale for better quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      scrollX: 0,
      scrollY: -window.scrollY,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
    });

    const { jsPDF } = window.jspdf;

    // A4 Dimensions in mm
    const a4Width = 210;
    const a4Height = 297;

    const pdf = new jsPDF("p", "mm", "a4");

    const pages = paginateCanvasToPdf({
      canvas,
      element,
      pdfWidth: a4Width,
      pageHeightMm: a4Height,
    });

    pages.forEach((p, i) => {
      if (i > 0) pdf.addPage();
      pdf.addImage(p.sliceImgData, "JPEG", 0, 0, a4Width, p.sliceHeightMm);
    });

    return pdf.output("blob");
  };

  const handleDownloadSummary = async () => {
    const confirmed = window.confirm(
      "This will download a PDF summary of the entire assessment including all interventions across all domains. Do you want to continue?"
    );
    if (!confirmed) return;

    setIsGeneratingPdf(true);
    const previousState = openAllAccordions();

    try {
      const blob = await captureSummaryAsPdfBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setAccordionOpen(previousState);
      setIsGeneratingPdf(false);
    }
  };

  const handleShareMail = async () => {
    setShowShareModal(false);
    setIsGeneratingPdf(true);
    const previousState = openAllAccordions();

    try {
      // 1. Generate PDF
      const blob = await captureSummaryAsPdfBlob();

      // 2. Download the PDF locally (Required because browsers block direct email attachments)
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client || "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // 3. Open Email Client
      const subject = encodeURIComponent(`Assessment Summary ? ${client || "Client"}`);
      const body = encodeURIComponent(
        `Please find the assessment summary PDF attached.\n\nClient: ${client || "-"}\nAge Group: ${ageRange || "-"}\nGender: ${gender || "-"}\nSession Type: ${session || "-"}\n\n[Note: The PDF has been downloaded to your device. Please attach it manually to this email.]`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    } catch (err) {
      console.error("Mail share failed:", err);
      alert("Failed to generate PDF for email. Please try again.");
    } finally {
      setAccordionOpen(previousState);
      setIsGeneratingPdf(false);
    }
  };

  const handleShareWhatsApp = async () => {
    setShowShareModal(false);
    setIsGeneratingPdf(true);
    const previousState = openAllAccordions();

    try {
      // 1. Generate PDF
      const blob = await captureSummaryAsPdfBlob();

      // 2. Download the PDF locally
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assessment-summary-${client}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // 3. Open WhatsApp
      const message = encodeURIComponent(
        `Assessment Summary for ${client}\nAge Group: ${ageRange} | Session: ${session || currentSessionId}\n\n[Note: The PDF has been downloaded to your device. Please attach it manually in the chat.]`
      );
      window.open(`https://wa.me/?text=${message}`, "_blank");
    } catch (err) {
      console.error("WhatsApp share failed:", err);
      alert("Failed to generate PDF for WhatsApp. Please try again.");
    } finally {
      setAccordionOpen(previousState);
      setIsGeneratingPdf(false);
    }
  };

  // -- 5. Render Logic -----------------------------------------------------------

  const hasDescription = (item) =>
    item.intervention_title &&
    item.intervention_description !== null &&
    item.intervention_description !== undefined &&
    item.intervention_description !== "" &&
    !(Array.isArray(item.intervention_description) && item.intervention_description.length === 0);

  const renderGoalTabsAndInterventions = () => {
    if (!interventionsData || !interventionsData.session || !interventionsData.data) return null;

    const { data: interventionsMap } = interventionsData;

    const goalTabs = Object.keys(interventionsMap || {}).map(key => {
      const items = interventionsMap[key] || [];
      const firstItem = items[0] || {};
      const goalDomain = firstItem.goal_domain || "";
      const goalSkill = firstItem.goal_skill || "";
      const tabKey = goalSkill && goalSkill !== "null" ? goalSkill : goalDomain;
      const displayLabel = goalSkill && goalSkill !== "null" ? `${goalDomain} - ${goalSkill}` : goalDomain;
      return { key, tabKey, displayLabel };
    });

    const uniqueGoalTabs = goalTabs.filter((tab, index, self) => index === self.findIndex(t => t.tabKey === tab.tabKey));

    if (uniqueGoalTabs.length === 0) return <p>No goal skills defined.</p>;

    // -- PRINT / DOWNLOAD MODE ----------------------------------------------
    // While a PDF/share capture is in progress, render EVERY goal-domain
    // tab's interventions (not just the active one) so the download/share
    // actions produce one PDF containing all domains, fully expanded.
    // Each goal-domain section also gets "pdf-page-break" so the paginator
    // can prefer starting a new page at a domain heading instead of
    // mid-paragraph.
    if (isGeneratingPdf) {
      return (
        <>
          <div className="suggested-section1" style={{ marginTop: "20px" }}>
            <h2 className="suggested-title1">Suggested Interventions</h2>
          </div>

          {uniqueGoalTabs.map((tab) => {
            const interventions = interventionsMap[tab.key] || [];
            const valid = interventions.filter(hasDescription);

            // Group by excel_sheet
            const grouped = {};
            valid.forEach(item => {
              const sheet = item.excel_sheet || "General";
              if (!grouped[sheet]) grouped[sheet] = [];
              grouped[sheet].push(item);
            });

            return (
              <div key={tab.key} className="suggested-section1 pdf-page-break" style={{ marginTop: "15px" }}>
                <h3 className="togglee-btn3">{tab.displayLabel}</h3>
                {valid.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "30px 20px", background: "#f8f9fa", borderRadius: "8px", marginTop: "10px" }}>
                    <p style={{ margin: 0, fontSize: "16px", fontWeight: 500, color: "#666" }}>
                      No Suggested Interventions found for the selected goal
                    </p>
                  </div>
                ) : Object.entries(grouped).map(([sheet, items]) => (
                  <div key={sheet} style={{ marginTop: "8px" }}>
                    <h4 style={{ color: "#247A33", fontSize: "15px", fontWeight: "700", padding: "6px 12px", background: "#e8f5e9", borderRadius: "4px", borderLeft: "4px solid #247A33" }}>
                      {sheet}
                    </h4>
                    {items.map((item, idx) => (
                      <div key={item.id || idx} style={{ marginLeft: "12px", marginTop: "4px", padding: "6px 14px", borderLeft: "3px solid #aed581", background: "#f9fdf9", borderRadius: "4px" }}>
                        <p style={{ fontWeight: "600", fontSize: "14px", color: "#333", marginBottom: "4px" }}>{item.intervention_title}</p>
                        {Array.isArray(item.intervention_description) ? (
                          item.intervention_description.map((desc, i) => (
                            renderDescriptionText(desc, i, { fontSize: "13px", color: "#444", lineHeight: "1.5", textAlign: "justify", wordBreak: "break-word", overflowWrap: "break-word", marginBottom: "4px" })
                          ))
                        ) : (
                          renderDescriptionText(item.intervention_description, 0, { fontSize: "13px", color: "#444", lineHeight: "1.5", textAlign: "justify", wordBreak: "break-word", overflowWrap: "break-word" })
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            );
          })}
        </>
      );
    }

    // -- NORMAL (on-screen) MODE --------------------------------------------
    const { validSelected, grouped: selectedGrouped, sheetNames: selectedSheetNames } = activeTabData;

    return (
      <>
        <h1 className="sr-only">Suggested Interventions</h1>
        <div className="goal-toggle3">
          {uniqueGoalTabs.map((tab, index) => (
            <span
              key={index}
              className={`toggle-btn3 ${activeGoalTab === tab.tabKey ? "active" : ""}`}
              role="button"
              tabIndex={0}
              aria-pressed={activeGoalTab === tab.tabKey}
              onClick={() => {
                setActiveGoalTab(tab.tabKey);
                setActiveSheet(null);
                setActiveIntTitle(null);
                setAccordionOpen({});
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActiveGoalTab(tab.tabKey);
                  setActiveSheet(null);
                  setActiveIntTitle(null);
                  setAccordionOpen({});
                }
              }}
            >
              {tab.displayLabel}
            </span>
          ))}
        </div>

        <div className="suggested-section1">
          <div className="question-cardtop" style={{ borderLeft: "5px solid #288537", marginBottom: "0px", color: "#288537", fontWeight: "700", padding: "15px" }}>
            The following suggested interventions are based on your responses throughout the pathway and are not automatically validated for errors.
          </div>
          <h2 className="suggested-title1">Suggested Interventions</h2>

          {validSelected.length === 0 ? (
            <div style={{ textAlign: "center", padding: "30px 20px", background: "#f8f9fa", borderRadius: "8px", marginTop: "15px" }}>
              <p style={{ margin: 0, fontSize: "16px", fontWeight: 500, color: "#666" }}>
                No Suggested Interventions found for the selected goal
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "0", marginTop: "15px", minHeight: "400px" }}>
              {/* -- MOBILE FLOATING MENU BUTTON ---------------------------- */}
              <button
                className="interventions-mobile-menu-btn"
                onClick={() => setMobileSidebarOpen(true)}
                aria-label="Open navigation sidebar"
                >
                  &#9776;
                </button>

              {/* -- MOBILE SIDEBAR OVERLAY --------------------------------- */}
              {mobileSidebarOpen && (
                <div className="interventions-mobile-overlay" onClick={() => setMobileSidebarOpen(false)} />
              )}

              {/* -- LEFT SIDEBAR NAV ---------------------------------------- */}
              <nav className={`interventions-sidebar ${mobileSidebarOpen ? "mobile-open" : ""}`} aria-label="Intervention sections navigation">
                <div className="interventions-sidebar-header">
                  <span>Navigation</span>
                  <button className="interventions-sidebar-close" onClick={() => setMobileSidebarOpen(false)} aria-label="Close navigation sidebar">&#10005;</button>
                </div>
                {selectedSheetNames.map((sheet) => {
                  const items = selectedGrouped[sheet] || [];
                  const sheetOpen = activeSheet === sheet;
                  return (
                    <div key={sheet}>
                      <div
                        className={`interventions-sidebar-sheet ${sheetOpen ? "active" : ""}`}
                        role="button"
                        tabIndex={0}
                        aria-expanded={sheetOpen}
                        onClick={() => {
                          setActiveSheet(sheetOpen ? null : sheet);
                          if (!sheetOpen && items.length > 0) {
                            const targetId = `intv-${items[0].id}`;
                            const el = document.getElementById(targetId);
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setActiveSheet(sheetOpen ? null : sheet);
                            if (!sheetOpen && items.length > 0) {
                              const targetId = `intv-${items[0].id}`;
                              const el = document.getElementById(targetId);
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                            }
                          }
                        }}
                      >
                        <span>{sheet}</span>
                        <span style={{ fontSize: "11px" }}>{sheetOpen ? "\u25BC" : "\u25B6"}</span>
                      </div>
                      {sheetOpen && items.map((item) => (
                        <div
                          key={item.id}
                          className={`interventions-sidebar-title ${activeIntTitle === item.intervention_title ? "active" : ""}`}
                          role="button"
                          tabIndex={0}
                          aria-current={activeIntTitle === item.intervention_title ? "true" : undefined}
                          onClick={() => {
                            setActiveIntTitle(item.intervention_title);
                            setMobileSidebarOpen(false);
                            const targetId = `intv-${item.id}`;
                            const el = document.getElementById(targetId);
                            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setActiveIntTitle(item.intervention_title);
                              setMobileSidebarOpen(false);
                              const targetId = `intv-${item.id}`;
                              const el = document.getElementById(targetId);
                              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                            }
                          }}
                          title={item.intervention_title}
                        >
                          <span className="interventions-sidebar-dot">&#8226;</span>
                          <span className="interventions-sidebar-title-text">{item.intervention_title}</span>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </nav>

              {/* -- RIGHT CONTENT: all interventions for active goal ---- */}
              <div className="interventions-scroll-content" ref={scrollContentRef}>
                {selectedSheetNames.map((sheet) => {
                  const items = selectedGrouped[sheet] || [];
                  return (
                    <div key={sheet} className="interventions-sheet-section">
                      <h4 className="interventions-sheet-heading">{sheet}</h4>
                      {items.map((item) => (
                        <div
                          key={item.id}
                          id={`intv-${item.id}`}
                          className="interventions-card"
                        >
                          <h3 className="interventions-card-title">{"\u2714"} {item.intervention_title}</h3>
                          <div className="interventions-card-body">
                            {Array.isArray(item.intervention_description) ? (
                              item.intervention_description.map((desc, i) => (
                                renderDescriptionText(desc, i, undefined, "interventions-card-desc")
                              ))
                            ) : (
                              renderDescriptionText(item.intervention_description, 0, undefined, "interventions-card-desc")
                            )}
                          </div>
                          {!isGeneratingPdf && item.image_url && (
                            <img
                              src={assetUrl(item.image_url)}
                              alt=""
                              className="interventions-card-img"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <style>{`
        /* summ-* summary card styles (local copy so summarypage.css is untouched) */
        .summ-container11 {
          width: 100%;
          margin-top: 0px;
          color: #333;
          padding: 0 0 15px 0;
        }
        .sum-cont11 {
          flex: 1;
          padding: 0px 20px 10px 0px;
          width: 100%;
        }
        .summ-client-row {
          text-align: center;
          font-size: 18px;
          font-weight: 700;
          color: #247a33;
          margin-bottom: 14px;
        }
        .summ-meta-row {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          justify-content: flex-start;
          align-items: center;
          margin-bottom: 18px;
          text-align: left;
        }
        .summ-meta-label {
          font-size: 14px;
          font-weight: 600;
          color: #247a33;
        }
        .summ-meta-value {
          font-size: 14px;
          font-weight: 500;
          color: var(--cpa-primary, #247A3D);
        }
        .summ-meta-pill {
          display: inline-flex;
          align-items: center;
          background: #ffffff;
          border: 1px solid #dcdcdc;
          border-radius: 50px;
          padding: 5px 12px;
        }
        .summ-meta-plain {
          border: none;
          background: transparent;
          border-radius: 0;
          padding: 0;
        }
        .summ-meta-green .summ-meta-label {
          color: var(--cpa-primary, #247A3D);
        }
        .summ-section-title {
          font-size: 18px;
          font-weight: 700;
          color: #323C43;
          margin: 6px 0 14px 0;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .summ-findings-block {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 10px;
        }
        .summ-finding-row {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 12px;
          padding: 8px 12px;
          background: #ffffff;
          border: 1px solid #dcdcdc;
          border-radius: 4px;
          word-wrap: break-word;
          overflow-wrap: break-word;
        }
        .summ-finding-label {
          color: #323c43;
          font-weight: 700;
          font-size: 15px;
          white-space: pre-wrap;
        }
        .summ-value-pill {
          display: inline-block;
          background: #ffffff;
          border: 1px solid #dcdcdc;
          border-radius: 4px;
          padding: 4px 10px;
          font-size: 14px;
          color: #323c43;
          font-weight: 500;
        }
        .summ-finding-row .summ-value-pill {
          border: none;
          background: transparent;
          border-radius: 0;
          padding: 0;
        }
        /* Rich text coming from the intervention_description editor.
           Declared here (not in summarypage.css) so the on-screen view and the
           html2canvas snapshot of it lay the markup out identically. */
        .interv-desc-rich p,
        .interv-desc-rich div {
          margin: 0 0 6px 0;
        }
        .interv-desc-rich p:last-child,
        .interv-desc-rich div:last-child,
        .interv-desc-rich ul:last-child,
        .interv-desc-rich ol:last-child {
          margin-bottom: 0;
        }
        .interv-desc-rich ul,
        .interv-desc-rich ol {
          margin: 0 0 6px 0;
          padding-left: 20px;
        }
        .interv-desc-rich li {
          margin: 0 0 2px 0;
        }
        .interv-desc-rich b,
        .interv-desc-rich strong {
          font-weight: 700;
        }
        .interv-desc-rich i,
        .interv-desc-rich em,
        .interv-desc-rich cite {
          font-style: italic;
        }
        .interv-desc-rich u,
        .interv-desc-rich ins {
          text-decoration: underline;
        }
        .interv-desc-rich sub,
        .interv-desc-rich sup {
          font-size: 75%;
          line-height: 0;
        }
        .interv-desc-link {
          color: #1a6fb5;
          text-decoration: underline;
          word-break: break-all;
          overflow-wrap: break-word;
        }
        @media (max-width: 768px) {
          .summ-client-row {
            font-size: 20px;
          }
          .summ-meta-row {
            gap: 10px;
          }
          .summ-section-title {
            font-size: 16px;
          }
          .summ-finding-label {
            font-size: 14px;
          }
        }
      `}</style>
      <div
        style={{
          width: "95%",
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        <div className="summary-container111" ref={summaryRef}>
          <div className="sum-cont1">
            <div className="summ-container11">
              <div className="sum-cont11">

                {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                <div className="summ-client-row">
                  {interventionsData?.session?.client_name || " "}'s Goals
                </div>

                {/* Section 1 - Row 2: Metadata pills */}
                <div className="summ-meta-row" >
                  <span className="summ-meta-pill summ-meta-plain">
                    <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                    <span className="summ-meta-value" style={{ color: "#247A33" }}>
                      {(() => {
                        const code = String(interventionsData?.session?.client_code || "");
                        const digits = code.replace(/\D/g, "").slice(0, 8);
                        if (digits.length === 8) {
                          const dd = digits.slice(0, 2);
                          const mm = digits.slice(2, 4);
                          const yyyy = digits.slice(4, 8);
                          const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                          const mIdx = parseInt(mm, 10) - 1;
                          return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                        }
                        return "";
                      })()}
                    </span>
                  </span>
                  <span className="summ-meta-pill summ-meta-green">
                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                  </span>
                  <span className="summ-meta-pill summ-meta-green">
                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ""}</span>
                  </span>
                </div>

                {/* Section 1 - Row 3: Section title */}
                <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                {/* Section 1 - Row 4: Topography + Severity */}
                <div className="summ-findings-block">
                  <div className="summ-finding-row">
                    <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                    <strong className="summ-finding-label">Topography:&nbsp;</strong>
                    <span className="summ-value-pill">{interventionsData?.session?.topography || ""}</span>
                  </div>
                  <div className="summ-finding-row">
                    <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                    <strong className="summ-finding-label">Severity:&nbsp;</strong>
                    <span className="summ-value-pill">{interventionsData?.session?.severity || ""}</span>
                  </div>
                </div>

                {/* Section 1 - Row 5: Early indicators of comorbidities */}
                <div className="summ-finding-row">
                  <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                  <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                  <span className="summ-value-pill">
                    {interventionsData?.session?.comorbidities?.length
                      ? interventionsData.session.comorbidities.map(item => item.type).join(", ")
                      : ""}
                  </span>
                </div>
                <div className="summ-finding-row">
                  <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                  <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                  <span className="summ-value-pill">
                    {(() => {
                      const factors = interventionsData?.session?.goal_limiting_factors || [];
                      return factors.join(", ");
                    })()}
                  </span>
                </div>

                {/* Section 1 - Row 6: Goals */}
                <div className="summ-finding-row">
                  <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                  <strong className="summ-finding-label">Goals:&nbsp;</strong>
                  <span className="summ-value-pill">
                    {buildDomainTagsFromDataMap(interventionsData?.data).join(", ")}
                  </span>
                </div>
              </div>
            </div>

            {renderGoalTabsAndInterventions()}


          </div>
        </div>
        <div className="summary-footer5">
          {/* ── REVISE SELECTED INTERVENTION / SET GOAL PROCEDURE ──
              Review Smart Goal (goal review -> /start-assessment -> this page,
              always carrying smartGoal=1) is the only run that revises an
              existing goal, so it gets "Revise Selected Intervention" and its
              /updategoal keeps the goal-session context. Every other run -
              plain Review and Initial - shows "Set Goal Procedure" and lets
              /updategoal set a fresh procedure for the session. */}
          <button
            className="secondary-btn"
            onClick={() => {
              // Review Smart Goal keeps its context so Updategoal submits back
              // to the goal being reviewed and returns to the goal download.
              //
              // The page session stays the REVIEW session (its answers produced
              // the Suggested Interventions shown above), while the goal session
              // rides along separately. /updategoal needs both: the review
              // session for the intervention catalogue, and the goal session for
              // the existing SMART goal it pre-fills and overwrites. Collapsing
              // them to the goal session showed the originating pathway's
              // group names here, which are not the ones just reviewed.
              const pageSessionId = isSmartGoal
                ? (currentSessionId || reviseSessionId)
                : reviseSessionId;
              const params = new URLSearchParams({ sessionId: pageSessionId || "" });
              if (isFromReview || isSmartGoal) params.set("fromReview", "1");
              if (isSmartGoal) {
                params.set("smartGoal", "1");
                if (goalSessionId) params.set("goalSessionId", goalSessionId);
                if (urlParams.get("clientId")) params.set("clientId", urlParams.get("clientId"));
              }
              navigate(`/updategoal?${params.toString()}`);
            }}
            disabled={isSmartGoal ? !reviseSessionId : interventionsData?.goals?.length > 0}
          >
            {isSmartGoal
            ? "Revise Selected Intervention"
            : "Set Goal Procedure"}
          </button>
         
          <button
            className="primary-btn"
            onClick={handleDownloadSummary}
            disabled={isGeneratingPdf}
          >
            Download Summary <FaDownload className="btn-icon" aria-hidden="true" />
          </button>
          <button
            className="secondary-btn"
            onClick={() => navigate("/Home")}
          >
            Exit
          </button>
        </div>
      </div>
      {/* PDF Loading Overlay */}
      {isGeneratingPdf && <LoadingOverlay />}

      {/* Share Modal */}
      {showShareModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Share assessment summary"
          style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.45)", zIndex: 10001, display: "flex", justifyContent: "center", alignItems: "center" }}
          onClick={() => setShowShareModal(false)}
        >
          <div
            style={{ background: "#fff", borderRadius: "16px", padding: "32px 28px", minWidth: "300px", boxShadow: "0 8px 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column", gap: "16px", alignItems: "stretch" }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "bold", color: "#222", textAlign: "center" }}>Share Assessment Summary</h3>
            <p style={{ margin: 0, fontSize: "13px", color: "#666", textAlign: "center" }}>The summary will be downloaded, and then you can attach it.</p>

            <button
              onClick={handleShareMail}
              style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
              onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
            >
              <span style={{ fontSize: "26px" }}>&#9993;</span>
              Share via Mail
            </button>

            <button
              onClick={handleShareWhatsApp}
              style={{ display: "flex", alignItems: "center", gap: "14px", padding: "14px 20px", border: "1.5px solid #e0e0e0", borderRadius: "10px", background: "#f7f7f7", cursor: "pointer", fontSize: "15px", fontWeight: "600", color: "#333", transition: "background 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#eaf4ea"}
              onMouseLeave={e => e.currentTarget.style.background = "#f7f7f7"}
            >
              <span style={{ fontSize: "26px" }}>&#128172;</span>
              Share via WhatsApp
            </button>

            <button
              onClick={() => setShowShareModal(false)}
              style={{ marginTop: "4px", background: "none", border: "none", color: "var(--cpa-gray-2, #626A6F)", cursor: "pointer", fontSize: "14px", textAlign: "center" }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
}
